// Shared helpers for building/parsing the TOEFL model-test blueprint stored on Quiz.metadata.
// The taker (ToeflModelTest) renders section → module → task from this; the admin Add/Edit
// forms produce it. Keep the shape identical to seed_toefl_mt1.py's _blueprint().

// Section config — mirrors the seed (navigation + per-section scoring + module limits + defaults).
export const TOEFL_SECTIONS = [
    { key: 'reading', name: 'Reading', maxModules: 2, navigation: 'back_within_module', scoring: { scale: [[0, 0], [13, 15], [26, 30]] }, defaultMinutes: 15 },
    { key: 'listening', name: 'Listening', maxModules: 2, navigation: 'forward_only_per_question', scoring: { scale: [[0, 0], [6, 15], [12, 30]] }, defaultMinutes: 15 },
    { key: 'writing', name: 'Writing', maxModules: 1, scoring: { scored_tasks: ['build_sentence'] }, defaultMinutes: 23 },
    { key: 'speaking', name: 'Speaking', maxModules: 1, scoring: { unscored: true }, defaultMinutes: 8 },
];

export const sectionByName = (name) => TOEFL_SECTIONS.find(s => s.name === name);

// A bucket is the assignable target used by the quiz forms. Keys are STABLE regardless of the
// current module count so switching 1↔2 modules never orphans assigned questions: sections that
// can have multiple modules (Reading/Listening) always use "Reading — Module 1"; single-module
// sections (Writing/Speaking) use the plain section name.
export function bucketKey(sectionName, moduleIdx) {
    const sec = sectionByName(sectionName);
    const multi = sec && sec.maxModules > 1;
    return multi ? `${sectionName} — Module ${moduleIdx + 1}` : sectionName;
}
export function parseBucketKey(bucket) {
    const m = /^(.*) — Module (\d+)$/.exec(bucket);
    if (m) return { sectionName: m[1], moduleIdx: Number(m[2]) - 1 };
    return { sectionName: bucket, moduleIdx: 0 };
}

// The ordered list of buckets for a given module-count config.
export function bucketsFor(moduleCount) {
    const out = [];
    TOEFL_SECTIONS.forEach(sec => {
        const count = sec.maxModules === 1 ? 1 : (moduleCount[sec.key] || 1);
        for (let i = 0; i < count; i += 1) out.push({ sectionKey: sec.key, sectionName: sec.name, moduleIdx: i, count, key: bucketKey(sec.name, i, count) });
    });
    return out;
}

// Derive a taker task type + display title from a question.
export function taskInfo(q) {
    const t = q.metadata && q.metadata.toefl && q.metadata.toefl.task;
    if (q.question_type === 'BUILD_SENTENCE') return { task: 'build_sentence', title: 'Build a Sentence' };
    if (q.question_type === 'MCQ') return { task: 'mcq', title: q.topic || 'Multiple choice' };
    if (t) return { task: t, title: q.topic || t };
    if ((q.topic || '').toLowerCase().includes('complete words')) return { task: 'complete_words', title: 'Complete the Words' };
    if (q.subject === 'Speaking') return { task: 'speaking_interview', title: q.topic || 'Speaking' };
    if (q.subject === 'Writing') return { task: 'writing_email', title: q.topic || 'Writing' };
    return { task: 'mcq', title: q.topic || 'Question' };
}

// Index an existing blueprint's tasks by each question_id, capturing fields NOT reconstructable
// from the questions alone (layout/template/table for completion, group_id, task/title). Used on
// edit to carry these through unchanged.
export function origTaskByQid(metadata) {
    const map = {};
    (metadata?.sections || []).forEach((sec) => {
        (sec.modules || []).forEach((mod) => {
            (mod.tasks || []).forEach((t, i) => {
                const id = `${sec.key || sec.name}:${mod.key || mod.name}:${i}`;
                (t.question_ids || []).forEach((qid) => {
                    map[qid] = {
                        _id: id, task: t.task, title: t.title,
                        layout: t.layout, template: t.template, table: t.table, group_id: t.group_id,
                    };
                });
            });
        });
    });
    return map;
}

// Cluster an ordered list of question objects into tasks. When `origByQid` is supplied (edit),
// every question already in the quiz keeps its ORIGINAL task, carried through VERBATIM
// (task/title/layout/template/table/group_id never re-derived) — so "Save Changes" cannot alter
// an existing task. Grouping is by original task identity globally. New questions cluster
// contiguously by group_id (audio/passage), else by (task type + topic).
export function buildTasks(questionObjs, origByQid) {
    const tasks = [];
    const origTaskByKey = {};
    (questionObjs || []).forEach((q) => {
        const orig = origByQid && origByQid[q.question_id];
        if (orig) {
            const key = `orig:${orig._id}`;
            let task = origTaskByKey[key];
            if (!task) {
                task = { _key: key, task: orig.task, title: orig.title, question_ids: [] };
                if (orig.layout !== undefined) task.layout = orig.layout;
                if (orig.template !== undefined) task.template = orig.template;
                if (orig.table !== undefined) task.table = orig.table;
                if (orig.group_id !== undefined) task.group_id = orig.group_id;
                origTaskByKey[key] = task;
                tasks.push(task);
            }
            task.question_ids.push(q.question_id);
            return;
        }
        const info = taskInfo(q);
        const groupKey = q.group_id ? `new:g${q.group_id}` : `new:t${info.task}:${q.topic || ''}`;
        const last = tasks[tasks.length - 1];
        if (last && last._key === groupKey) {
            last.question_ids.push(q.question_id);
        } else {
            const task = { _key: groupKey, task: info.task, title: info.title, question_ids: [q.question_id] };
            if (q.group_id) task.group_id = q.group_id;
            tasks.push(task);
        }
    });
    return tasks.map(({ _key, ...t }) => t);
}

const moduleName = (sec, count, idx) => {
    if (count > 1) return `Module ${idx + 1}`;
    if (sec.key === 'writing') return 'Writing Tasks';
    if (sec.key === 'speaking') return 'Speaking Tasks';
    return 'Module 1';
};

// Build the full blueprint + the per-section question_ids (for QuizQuestionSet) + total minutes.
// questionsByBucket: { "Reading — Module 1": [question objects], "Writing": [...], ... }
export function buildBlueprint({ moduleCount, durations, questionsByBucket, existingMetadata }) {
    const origByQid = existingMetadata ? origTaskByQid(existingMetadata) : null;
    const questionIdsBySection = {};
    let totalMinutes = 0;
    const sections = TOEFL_SECTIONS.map((sec) => {
        const count = sec.maxModules === 1 ? 1 : (moduleCount[sec.key] || 1);
        const modules = [];
        const secIds = [];
        for (let i = 0; i < count; i += 1) {
            const bucket = bucketKey(sec.name, i, count);
            const qObjs = questionsByBucket[bucket] || [];
            if (qObjs.length === 0) continue;
            secIds.push(...qObjs.map(q => q.question_id));
            const mins = Number(durations[bucket]) || sec.defaultMinutes;
            totalMinutes += mins;
            modules.push({ key: `m${i + 1}`, name: moduleName(sec, count, i), duration_minutes: mins, tasks: buildTasks(qObjs, origByQid) });
        }
        if (secIds.length) questionIdsBySection[sec.name] = secIds;
        const out = { key: sec.key, name: sec.name, modules };
        if (sec.navigation) out.navigation = sec.navigation;
        if (sec.scoring) out.scoring = sec.scoring;
        return out;
    }).filter(s => s.modules.length > 0);

    return { blueprint: { test_kind: 'toefl_model_test', sections }, questionIdsBySection, totalMinutes };
}

// Reverse of buildBlueprint for the edit form: reconstruct buckets/moduleCount/durations from an
// existing blueprint + the quiz's questions ({section: [question objects]}).
export function bucketsFromBlueprint(metadata, questionsBySection) {
    const allById = {};
    Object.values(questionsBySection || {}).forEach(arr => (arr || []).forEach(q => { allById[q.question_id] = q; }));
    const questionsByBucket = {};
    const moduleCount = {};
    const durations = {};
    (metadata?.sections || []).forEach((sec) => {
        const count = (sec.modules || []).length || 1;
        moduleCount[sec.key] = count;
        (sec.modules || []).forEach((mod, idx) => {
            const bucket = bucketKey(sec.name, idx, count);
            const qids = (mod.tasks || []).flatMap(t => t.question_ids || []);
            questionsByBucket[bucket] = qids.map(id => allById[id]).filter(Boolean);
            durations[bucket] = mod.duration_minutes;
        });
    });
    return { questionsByBucket, moduleCount, durations };
}
